import React, {useEffect, useRef} from 'react'
import socket from 'src/socketio/socketio.js'

import {FormRow, Input} from './index.js'
import guid from 'utils/guid.js'
import {Address} from 'types/types.js'
import {FieldProps} from './types.js'

// A group of inputs (postcode, number, addition) that each have their own label, so label and info make
// no sense for the group: they are accepted as part of FieldProps but not rendered. style is not used either.
// disabled and readOnly are forwarded to all inputs
interface PostalCodeAutocompleteProps extends FieldProps {
    id?: string
    address: Address
    onChange?: (address: Address) => void
    changeFocusAfterPostalCode?: boolean
}

const POSTAL_CODE_REGEX = /^[0-9]{4} [A-Z]{2}$/
const POSTAL_CODE_NO_SPACE_REGEX = /^[0-9]{4}[A-Z]{2}$/
const POSTAL_CODE_TRAILING_SPACE_REGEX = /^[0-9]{4} [A-Z]{2} $/

const PostalCodeAutocomplete = ({id = '', address, disabled = false, readOnly = false, onChange, changeFocusAfterPostalCode = true}: PostalCodeAutocompleteProps) => {
    const lastCallbackIdRef = useRef<string>('')
    const prevAddressRef = useRef({postalCode: address.postalCode, nr: address.nr, addition: address.addition, country: address.country})

    // Set when the user changes the postal code, number or addition
    const userEditedRef = useRef(false)

    // Autocomplete address after the user changed the postal code, number or addition. Not on mount or when an address
    // is loaded (e.g. an imported order): it is kept as it was saved instead of being overwritten (and marked as
    // changed), otherwise a correct street is replaced by the street of a mistyped postal code
    useEffect(() => {
        const prev = prevAddressRef.current
        prevAddressRef.current = {postalCode: address.postalCode, nr: address.nr, addition: address.addition, country: address.country}

        if (prev.postalCode !== address.postalCode || prev.nr !== address.nr || prev.addition !== address.addition || prev.country !== address.country) {
            // A pending lookup is for the previous postal code, so its response must not overwrite the new address
            // (e.g. an address chosen from the address book)
            lastCallbackIdRef.current = ''

            if (userEditedRef.current) {
                userEditedRef.current = false
                autocomplete(address)
            }
        }
    }, [address.postalCode, address.nr, address.addition, address.country])

    const autocomplete = (address: Address) => {
        // Disabled and read only never change the address, also not when it is changed from outside
        if (!disabled && !readOnly && address.country === 'NL' && POSTAL_CODE_REGEX.test(address.postalCode) && address.nr) {
            const callbackId = guid.generate()
            lastCallbackIdRef.current = callbackId

            socket.emit('addresses.autocomplete', sessionStorage.token || localStorage.mtoken, {address, callbackId}, (err: Error | null, data: {address: Address | null, callbackId: string} | null) => {
                // Ignore outdated callbacks
                if (err || !data || data.callbackId !== lastCallbackIdRef.current) {
                    return
                }

                const addr = data.address

                if (!addr) {
                    return
                }

                // Only pass the fields the lookup sets: callers merge this into their current address,
                // so edits to other fields made while the request was pending are kept
                const updatedAddress: Partial<Address> = {position: address.position}

                if (addr.street) {
                    updatedAddress.street = addr.street
                }

                if (addr.city) {
                    updatedAddress.city = addr.city
                }

                updatedAddress.isBusiness = !!addr.isBusiness

                if (addr.position?.lat && addr.position?.lng) {
                    updatedAddress.position = addr.position
                }

                if (typeof addr.showWarning !== 'undefined') {
                    updatedAddress.showWarning = addr.showWarning
                }

                onChange?.(updatedAddress as Address)
            })
        }
    }

    const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        userEditedRef.current = true

        const updatedAddress = {...address}
        let postalCode = event.target.value.toUpperCase().replace(/  +/g, ' ')

        // Auto-format postal code: add space between digits and letters
        if (POSTAL_CODE_NO_SPACE_REGEX.test(postalCode)) {
            postalCode = `${postalCode.substring(0, 4)} ${postalCode.substring(4, 6)}`
        }

        // Remove trailing space
        if (POSTAL_CODE_TRAILING_SPACE_REGEX.test(postalCode)) {
            postalCode = postalCode.trim()
        }

        updatedAddress.postalCode = postalCode

        // Remove position when postal code changes
        delete updatedAddress.position

        if (POSTAL_CODE_REGEX.test(postalCode) && changeFocusAfterPostalCode) {
            document.getElementById(`nr${id}`)?.focus()
        }

        onChange?.(updatedAddress)
    }

    const handleNrChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        userEditedRef.current = true

        const updatedAddress = {...address}
        updatedAddress.nr = event.target.value.toUpperCase()

        // Remove position when house number changes
        delete updatedAddress.position

        onChange?.(updatedAddress)
    }

    const handleAdditionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        userEditedRef.current = true

        const updatedAddress = {...address}
        let addition = event.target.value.toUpperCase()

        // Auto-format addition: add hyphen prefix for numbers
        if (addition.length > 0 && /[0-9]/.test(addition[0])) {
            addition = `-${addition}`
        } else if (
            addition.length > 1 &&
            addition[0] === '-' &&
            /[^0-9]/.test(addition[1])
        ) {
            // Remove hyphen if followed by non-number
            addition = addition.substring(1)
        }

        updatedAddress.addition = addition

        onChange?.(updatedAddress)
    }

    return (
        <FormRow>
            <Input
                id={`postalCode${id}`}
                style={{minWidth: 100}}
                label='Postcode'
                info={address.postalCode &&
                    address.country === 'NL' &&
                    address.showWarning ?
                    'Dit adres staat niet in onze database, controleer of dit adres geldig is.' :
                    undefined}
                infoIcon='mdi mdi-map-marker-alert'
                value={address.postalCode}
                disabled={disabled}
                readOnly={readOnly}
                onChange={handlePostalCodeChange}
            />

            <FormRow style={{flex: 1, minWidth: 202}}>
                <Input
                    inputMode='numeric'
                    id={`nr${id}`}
                    style={{minWidth: 100}}
                    label='Huisnummer'
                    value={address.nr}
                    disabled={disabled}
                    readOnly={readOnly}
                    onChange={handleNrChange}
                />

                <Input
                    style={{minWidth: 90}}
                    label='Toevoeging'
                    value={address.addition}
                    disabled={disabled}
                    readOnly={readOnly}
                    onChange={handleAdditionChange}
                />
            </FormRow>
        </FormRow>
    )
}

export default PostalCodeAutocomplete
